import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import classNames from "classnames";
import ReactMapGL, { Marker } from "react-map-gl";
import styles from "../Forms.module.scss";
import pinStyles from "screens/modal/PlacePin/PlacePin.module.scss";
import Pin from "assets/Pin";
import { IUserSite, db } from "database";
import locationUtils from "../../utils/locationutils";
import CreateSiteSchema from "./CreateSiteSchema";
import { getEnvs } from "../../env";

type CreateSiteFormProps = {
	openTab: (tabType: string) => void;
	close: () => void;
	setTouched: (touched: boolean) => void;
	closeForm: () => void;
	resetValues: boolean;
	placePin: () => void;
	editSite?: boolean;
	doUpdate: () => void;
	site?: IUserSite;
	markerPosition: {
		latitude: number;
		longitude: number;
	};
};

const CreateSiteForm: React.FC<CreateSiteFormProps> = ({
	openTab,
	close,
	setTouched,
	closeForm,
	resetValues,
	placePin,
	doUpdate,
	site,
	editSite = false,
	markerPosition,
}) => {
	const viewport = {
		latitude: markerPosition.latitude,
		longitude: markerPosition.longitude,
		zoom: 14,
	};
	const [toastMessage, setToastMessage] = useState("");
	const [openToast, setOpenToast] = useState(false);
	const [confirmCancelButtons, setConfirmCancelButtons] = useState(false);
	const [ELRDetails, setELRDetails] = useState<any>();
	const [showSubmitButton, setShowSubmitButton] = useState(false);
	const [touchedPlacePin, setTouchedPlacePin] = useState(false);
	const [photos, setPhotos] = useState<any[]>([]);
	const [confirmRemovePhotos, setConfirmRemovePhotos] = useState(false);
	const [selectedPhoto, setSelectedPhoto] = useState<string | undefined>();
	const [openPhotoViewer, setOpenPhotoViewer] = useState(false);

	const postCodeRegex = new RegExp(
		/^(GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4})$/i
	);

	const [SERVER_URL, setSERVER_URL] = useState("");
	const [MAP_BOX_API, setMAP_BOX_API] = useState("");

	useEffect(() => {
		setPhotos(site?.photos || []);
	}, [site]);

	useEffect(() => {
		async function setServerUrl() {
			try {
				const envvars: any = await getEnvs();
				setSERVER_URL(envvars.REACT_APP_SERVER_URL_API);
				setMAP_BOX_API(envvars.REACT_APP_MAP_BOX_API);
			} catch {
				setSERVER_URL("http://localhost:4000");
				setMAP_BOX_API("");
			}
		}

		setServerUrl();
	}, []);

	const evaluateClass = (formik: any, field: string): string => {
		if (!formik.values[field]) return "";
		let rtn: string = "";
		if (formik.touched[field]) {
			if (formik.errors[field]) {
				rtn = styles.error;
			} else {
				rtn = styles.success;
			}
		} else {
			rtn = "";
		}
		return rtn;
	};

	const validate = (values: any) => {
		const errors: any = {};

		if (values.sitePostCode) {
			if (!postCodeRegex.test(values.sitePostCode)) {
				errors.sitePostCode = "Invalid postCode: " + values.sitePostCode;
			}
		}
		if (
			(!values.latitude || !values.longitude) &&
			!values.sitePostCode &&
			(!values.siteMileage || !values.elr)
		) {
			errors.siteMileage =
				"Either mileage and elr, latitude and longitude, or postcode are required fields.";
			errors.elr =
				"Either mileage and elr, latitude and longitude, or postcode are required fields.";
			errors.sitePostCode =
				"Either mileage and elr, latitude and longitude, or postcode are required fields.";
		}
		return errors;
	};

	// Get ELR

	useEffect(() => {
		const ELR = locationUtils.NearbyELRsToLatLng(
			markerPosition.latitude,
			markerPosition.longitude
		);

		console.log("elr", ELR);
		if (touchedPlacePin) {
			setELRDetails(ELR.elrs[0]);
		}
		formik.validateForm();
	}, [touchedPlacePin, markerPosition]);

	const saveForm = (values: any) => {
		const obj = {
			userSiteId: site ? site.userSiteId : undefined,
			siteId: values.siteId,
			route: values.route,
			name: values.siteName,
			altName: values.altSiteName,
			operationalStatus: values.operationalStatus,
			siteType: values.siteType,
			address: site ? site.address : "",
			postcode: values.sitePostCode,
			elr: values.elr,
			mileage: values.siteMileage,
			latitude: markerPosition.latitude,
			longitude: markerPosition.longitude,
			photos: photos ? photos : null,
			comments: values.comments,
		}

		db.savedUserSites.put(obj).then();
	};

	const removePhoto = () => {
		if (selectedPhoto && photos) {
			let newPhotos = [...photos];
			const photoIdx = photos.indexOf(selectedPhoto);
			newPhotos.splice(photoIdx, 1);
			if (!newPhotos.length) {
				setOpenPhotoViewer(false);
			}
			setPhotos(newPhotos);
			updateSitePhotos(newPhotos);
		}
	};

	const updateSitePhotos = (newPhotos: any) => {
		const url = `${SERVER_URL}/updateUserPhotos`;
		fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
			body: JSON.stringify({
				userSiteId: site?.userSiteId,
				photos: newPhotos,
			}),
		})
			.then((res) => res.json())
			.then((v) => {
				setOpenPhotoViewer(false);
			});
	};

	const formatMilesAndYards = (MilesAndYards: any) => {
		let miles = "";
		let yards = "";
		if (MilesAndYards.miles.length > 2) {
			miles = `0${MilesAndYards.miles.substring(0, 2)}`;
		} else {
			miles = `00${MilesAndYards.miles.substring(0, 1)}`;
		}
		if (MilesAndYards.yards.length > 4) {
			yards = `0${MilesAndYards.yards.substring(0, 3)}`;
		} else if (MilesAndYards.yards.length > 3) {
			yards = `00${MilesAndYards.yards.substring(0, 2)}`;
		} else {
			yards = `000${MilesAndYards.yards.substring(0, 1)}`;
		}
		return `${miles}.${yards}`;
	};

	const createNewSite = (values: any, verified: boolean) => {
		const url = verified
			? `${SERVER_URL}/updateUserSite`
			: `${SERVER_URL}/uploadSite`;
		let formData = new FormData();
		formData.append("userSiteId", site ? site.userSiteId.toString() : "");
		formData.append("siteId", values.siteId);
		formData.append("route", values.route);
		formData.append("name", values.siteName);
		formData.append("altName", values.altSiteName);
		formData.append("operationalStatus", values.operationalStatus);
		formData.append("siteType", values.siteType);
		formData.append("elr", values.elr);
		formData.append("mileage", values.siteMileage);
		formData.append("postcode", values.sitePostCode);
		formData.append(
			"longitude",
			verified && site
				? site.longitude.toString()
				: markerPosition.longitude.toString()
		);
		formData.append(
			"latitude",
			verified && site
				? site.latitude.toString()
				: markerPosition.latitude.toString()
		);
		formData.append("comments", values.comments);
		formData.append(
			"createdDate",
			verified && site ? site.created.toString() : Date.now().toString()
		);

		fetch(url, {
			method: "POST",
			body: formData,
			credentials: "include",
		})
			.then((res) => res.json())
			.then((v) => {
				close();
			});
	};

	const formik = useFormik({
		initialValues: {
			siteId: site && site.siteId ? site.siteId.toString() : "",
			route: site && site.route ? site.route : "",
			siteName: site && site.name ? site.name : "",
			altSiteName: site && site.altName ? site.altName : "",
			siteType: site && site.siteType ? site.siteType : "",
			operationalStatus:
				site && site.operationalStatus ? site.operationalStatus : "",
			elr: site && site.elr ? site.elr : "",
			siteMileage: site && site.mileage ? site.mileage : "",
			latitude: site && site.latitude ? site.latitude : "",
			longitude: site && site.longitude ? site.longitude : "",
			sitePostCode: site && site.postcode ? site.postcode : "",
			comments: site && site.comments ? site.comments : "",
		},
		validationSchema: CreateSiteSchema,
		enableReinitialize: true,
		validate,
		onSubmit: async (values) => {
			if (site) {
				//delete site on verified
				await db.savedUserSites.delete(site?.userSiteId);
			} else {
				//check if site exists before creating a new one
				const userSitesWithTheSameName = await db.userSites
					.where({ name: values.siteName })
					.toArray();
				if (userSitesWithTheSameName.length) {
					setToastMessage(
						"This Site cannot be submitted. A Site with the same name already exists."
					);
					setOpenToast(true);
					return;
				}
			}

			editSite ? createNewSite(values, true) : createNewSite(values, false);
			doUpdate();
			close();
		},
	});

	const checkForNewLatLngOnManualElrMileageEntered = () => {
		if (values.siteMileage && values.elr && !errors.siteMileage) {
			const [miles, yards] = values.siteMileage.toString().split(".");
			const latlng = locationUtils.ELRToLatLng(values.elr, miles, yards);
			setFieldValue("latitude", parseFloat(latlng.lat).toFixed(4));
			setFieldValue("longitude", parseFloat(latlng.lng).toFixed(4));
			// fix lat long issue updating on elr/mileage
			markerPosition.longitude = parseFloat(latlng.lng);
			markerPosition.latitude = parseFloat(latlng.lat);
		}
	};

	const {
		errors,
		resetForm,
		handleBlur,
		handleChange,
		isSubmitting,
		values,
		setFieldValue,
		initialValues,
		touched,
	} = formik;
	useEffect(() => {
		resetForm();
	}, [resetValues]);
	useEffect(() => {
		if (site && site.route) {
			getRoutesSiteType(site.route);
		}
	}, [site]);
	useEffect(() => {
		if (ELRDetails) {
			setFieldValue("elr", ELRDetails.elr);
			setFieldValue("siteMileage", formatMilesAndYards(ELRDetails));
		}
		if (touchedPlacePin) {
			setFieldValue("latitude", markerPosition.latitude.toFixed(4));
			setFieldValue("longitude", markerPosition.longitude.toFixed(4));
		}
	}, [ELRDetails, touchedPlacePin]);

	setTouched(Object.keys(touched).length !== 0);
	const siteRoutes = [
		"Anglia Route",
		"Central Route",
		"East Coast Route",
		"East Midlands Route",
		"Kent Route",
		"North & East Route",
		"North West Route",
		"Scotland Route",
		"Sussex Route",
		"Wales Route",
		"WCML South Route",
		"Wessex Route",
		"Western Route",
	];

	const siteTypes = [
		{ route: "Anglia Route", type: "Anglia Critical Lineside" },
		{ route: "Anglia Route", type: "Anglia Lineside" },
		{ route: "Central Route", type: "Central Critical Lineside" },
		{ route: "Central Route", type: "Central Lineside" },
		{ route: "East Coast Route", type: "East Coast Critical Lineside" },
		{ route: "East Coast Route", type: "East Coast Lineside" },
		{ route: "East Midlands Route", type: "East Midlands Critical Lineside" },
		{ route: "East Midlands Route", type: "East Midlands Lineside" },
		{ route: "Kent Route", type: "Kent Critical Lineside" },
		{ route: "Kent Route", type: "Kent Lineside" },
		{
			route: "North & East Route",
			type: "North & East Critical Lineside",
		},
		{ route: "North & East Route", type: "North & East Lineside" },
		{
			route: "North West Route",
			type: "North West Critical Lineside",
		},
		{ route: "North West Route", type: "North West Lineside" },
		{ route: "Scotland Route", type: "Scotland Critical Lineside" },
		{ route: "Scotland Route", type: "Scotland Lineside" },
		{ route: "Sussex Route", type: "Sussex Critical Lineside" },
		{ route: "Sussex Route", type: "Sussex Lineside" },
		{ route: "Wales Route", type: "Wales Critical Lineside" },
		{ route: "Wales Route", type: "Wales Lineside" },
		{ route: "WCML South Route", type: "WCML South Critical Lineside" },
		{ route: "WCML South Route", type: "WCML South Lineside" },
		{ route: "Wessex Route", type: "Wessex Critical Lineside" },
		{ route: "Wessex Route", type: "Wessex Lineside" },
		{ route: "Western Route", type: "Western Critical Lineside" },
		{ route: "Western Route", type: "Western Lineside" },
	];

	const [selectedSiteTypes, setSelectedSiteTypes] = useState(
		siteTypes.map((siteType) => siteType.type)
	);

	const getRoutesSiteType = (selectedSiteRoute: string) => {
		let foundSiteTypes: string[] = [];
		siteTypes.find((siteType: any) => {
			if (siteType.route === selectedSiteRoute) {
				foundSiteTypes.push(siteType.type);
			}
		});
		setSelectedSiteTypes(foundSiteTypes);
		const siteTypesForAllRoutes = siteTypes.map((siteType) => siteType.route);
		if (!selectedSiteRoute || !foundSiteTypes.length) {
			setSelectedSiteTypes(siteTypesForAllRoutes);
		}
	};

	const operationalStatuses = [
		"Operational",
		"Out of use",
		"Redundant",
		"Removed",
		"Combined",
		"Demolished",
	];
	return (
		<>
			<div className={styles.Form}>
				<h2>{editSite ? "Verify Site" : "New Site"}</h2>
				<form onSubmit={formik.handleSubmit}>
					<label htmlFor="route">Route</label>
					<select
						className={evaluateClass(formik, "route")}
						id="route"
						name="route"
						onChange={(e) => {
							handleChange(e);
							formik.setFieldValue("siteType", "", true);
						}}
						onBlur={(v) => {
							handleBlur(v);
							getRoutesSiteType(values.route);
						}}
						value={values.route}
					>
						<option value="" disabled></option>
						{siteRoutes.map((route) => (
							<option value={route}>{route}</option>
						))}
					</select>
					{errors.route && touched.route ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.route}
						</span>
					) : (
						false
					)}

					<label htmlFor="siteName">Site Name</label>
					<div
						className={styles.toastInformation}
						onClick={() => openTab("toastSiteName")}
					>
						i
					</div>
					<input
						className={evaluateClass(formik, "siteName")}
						id="siteName"
						name="siteName"
						type="text"
						onChange={formik.handleChange}
						onBlur={handleBlur}
						value={formik.values.siteName}
					/>

					{errors.siteName && touched.siteName ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.siteName}
						</span>
					) : (
						false
					)}

					<label htmlFor="altSiteName">Site Alternative Name</label>
					<input
						className={evaluateClass(formik, "altSiteName")}
						id="altSiteName"
						name="altSiteName"
						type="text"
						onChange={formik.handleChange}
						value={formik.values.altSiteName}
						onBlur={handleBlur}
					/>
					{errors.altSiteName && touched.altSiteName ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.altSiteName}
						</span>
					) : (
						false
					)}

					<label htmlFor="siteType">Site Type</label>
					<select
						className={evaluateClass(formik, "siteType")}
						id="siteType"
						name="siteType"
						onChange={formik.handleChange}
						value={formik.values.siteType}
						onBlur={handleBlur}
					>
						<option value="" disabled></option>
						{selectedSiteTypes.map((siteType, idx) => {
							return (
								<option key={idx} value={siteType}>
									{siteType}
								</option>
							);
						})}
					</select>
					{errors.siteType && touched.siteType ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.siteType}
						</span>
					) : (
						false
					)}

					<label htmlFor="operationalStatus">Site Operational Status</label>
					<select
						className={evaluateClass(formik, "operationalStatus")}
						id="operationalStatus"
						name="operationalStatus"
						onChange={formik.handleChange}
						value={formik.values.operationalStatus}
						onBlur={handleBlur}
					>
						<option value=""></option>
						{operationalStatuses.map((status) => (
							<option value={status}>{status}</option>
						))}
					</select>
					{errors.operationalStatus && touched.operationalStatus ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.operationalStatus}
						</span>
					) : (
						false
					)}
					<label htmlFor="elr">Site ELR</label>
					<input
						className={evaluateClass(formik, "elr")}
						id="elr"
						name="elr"
						type="text"
						onChange={formik.handleChange}
						value={formik.values.elr}
						onBlur={(e) => {
							handleBlur(e);
							checkForNewLatLngOnManualElrMileageEntered();
						}}
					/>
					{errors.elr && touched.elr ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.elr}
						</span>
					) : (
						false
					)}
					<label htmlFor="siteMileage">Site Mileage</label>
					<input
						className={evaluateClass(formik, "siteMileage")}
						id="siteMileage"
						name="siteMileage"
						type="text"
						onChange={formik.handleChange}
						value={formik.values.siteMileage}
						onBlur={(e) => {
							handleBlur(e);
							checkForNewLatLngOnManualElrMileageEntered();
						}}
					/>
					{errors.siteMileage && touched.siteMileage ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.siteMileage}
						</span>
					) : (
						false
					)}
					<label htmlFor="latitude">Latitude</label>
					<input
						className={evaluateClass(formik, "latitude")}
						id="latitude"
						name="latitude"
						readOnly={true}
						type="text"
						value={formik.values.latitude}
						onBlur={handleBlur}
					/>
					{errors.latitude && touched.latitude ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.latitude}
						</span>
					) : (
						false
					)}
					<label htmlFor="longitude">longitude</label>
					<input
						className={evaluateClass(formik, "longitude")}
						id="longitude"
						name="longitude"
						readOnly={true}
						type="text"
						value={formik.values.longitude}
						onBlur={handleBlur}
					/>
					{errors.longitude && touched.longitude ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.longitude}
						</span>
					) : (
						false
					)}
					<label htmlFor="sitePostCode">Site Postcode</label>
					<input
						className={evaluateClass(formik, "sitePostCode")}
						id="sitePostCode"
						name="sitePostCode"
						type="text"
						onChange={formik.handleChange}
						value={formik.values.sitePostCode}
						onBlur={handleBlur}
					/>

					{errors.sitePostCode && touched.sitePostCode ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.sitePostCode}
						</span>
					) : (
						false
					)}

					<label htmlFor="location">Site Location</label>
					<div className={styles.MapContainer}>
						{MAP_BOX_API && (
							<ReactMapGL
								mapboxApiAccessToken={MAP_BOX_API}
								{...viewport}
								mapStyle="mapbox://styles/mapbox/streets-v11"
								width="100%"
								height="100%"
								onClick={() => {
									formik.validateForm();
									setTouchedPlacePin(true);
									placePin();
								}}
							>
								<Marker
									longitude={markerPosition.longitude}
									latitude={markerPosition.latitude}
									offsetTop={-20}
									offsetLeft={-10}
								>
									<Pin style={pinStyles.styledPin} />
								</Marker>
							</ReactMapGL>
						)}
					</div>
					{photos && photos.length > 0 && (
						<div
							className={styles.photoContainer}
							onClick={() => setOpenPhotoViewer(true)}
						>
							{photos.map((photo) => (
								<div className={styles.photoBox}>
									<img src={photo} className={styles.photo} />
								</div>
							))}
						</div>
					)}
					<label htmlFor="comments">Comments</label>
					<input
						className={evaluateClass(formik, "comments")}
						id="comments"
						name="comments"
						type="text"
						onChange={formik.handleChange}
						value={formik.values.comments}
						onBlur={handleBlur}
					/>
					{errors.comments && touched.comments ? (
						<span className={styles.Span}>
							<strong>Error:</strong> {errors.comments}
						</span>
					) : (
						false
					)}

					{confirmCancelButtons && initialValues !== values && (
						<div className={styles.buttonHolder}>
							<button
								className={styles.cancelButton}
								type="button"
								onClick={() => {
									setShowSubmitButton(false);
									setConfirmCancelButtons(false);
								}}
							>
								Cancel
							</button>
							<button
								className={styles.confirmButton}
								type="button"
								onClick={() => {
									close();
									setShowSubmitButton(false);
									setConfirmCancelButtons(false);
								}}
							>
								Delete Changes
							</button>
						</div>
					)}
					{showSubmitButton && (
						<div className={styles.buttonHolder}>
							<button
								className={styles.cancelButton}
								onClick={() => {
									values !== initialValues
										? setConfirmCancelButtons(true)
										: closeForm();
									setShowSubmitButton(false);
								}}
							>
								Cancel
							</button>
							<button
								disabled={isSubmitting}
								className={styles.confirmButton}
								type="submit"
							>
								{editSite ? "Confirm Verification" : "Confirm Submission"}
							</button>
						</div>
					)}
					{!showSubmitButton && (
						<div className={styles.buttonHolder}>
							{editSite && (
								<>
									<button
										className={styles.saveButton}
										type="button"
										onClick={() => {
											setToastMessage("Site saved");
											setOpenToast(true);
											saveForm(values);
										}}
									>
										Save, Verify Later
									</button>
									<button
										className={styles.verifyButton}
										onClick={() => {
											setShowSubmitButton(true);
										}}
									>
										Verify
									</button>
								</>
							)}
							{!editSite && (
								<button
									className={styles.submitButton}
									onClick={() => setShowSubmitButton(true)}
								>
									Submit
								</button>
							)}
						</div>
					)}
				</form>
			</div>
			<div
				className={classNames(styles.formToast, {
					[styles.toastEnter]: openToast,
				})}
			>
				<p>{toastMessage}</p>
				<button
					onClick={() => {
						setOpenToast(false);
						if (toastMessage === "Site saved") {
							close();
						}
					}}
				>
					Okay
				</button>
			</div>
			{confirmRemovePhotos && (
				<div
					className={classNames(styles.confirmRemoveMessage, {
						[styles.confirmRemoveMessageEnter]: confirmRemovePhotos,
					})}
				>
					<p>Are you sure you want to delete this photo?</p>
					<button
						onClick={() => {
							removePhoto();
							setConfirmRemovePhotos(false);
						}}
					>
						Okay
					</button>
					<button onClick={() => setConfirmRemovePhotos(false)}>Cancel</button>
				</div>
			)}
			{openPhotoViewer && (
				<div className={styles.photoViewer}>
					<div
						className={styles.closeButton}
						onClick={() => {
							setOpenPhotoViewer(false);
						}}
					/>
					{photos &&
						photos.map((photo) => (
							<div className={styles.photo}>
								<img src={photo} height={400} width={400} />
								<p
									className={styles.deleteButton}
									onClick={() => {
										setConfirmRemovePhotos(true);
										setSelectedPhoto(photo);
									}}
								>
									Delete
								</p>
							</div>
						))}
				</div>
			)}
		</>
	);
};

export default CreateSiteForm;
